import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Grid,
  TextField,
} from '@mui/material';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';

interface NDCreateProps {
  handleClose: () => void;
  nodeId: string;
}

export const NDCreate = ({ handleClose, nodeId }: NDCreateProps) => {
  const api = useApi();
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [deviceName, setDeviceName] = useState<string>('');
  const [ipAddr, setIpAddr] = useState<string>('');
  const [macAddr, setMacAddr] = useState<string>('');
  const [vendorName, setVendorName] = useState<string>('');

  const createDevice = useMutation(api.addNetworkDeviceApiV1AddNetworkDevicePost, {
    onSuccess() {
      setLoadingModalOpen(false);
      toast.success('A new device added successfully.');
      handleClose();
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      apiError(error);
    },
  });

  const isFormValid = useMemo(() => {
    return deviceName.trim() !== '' && ipAddr.trim() !== '';
  }, [deviceName, ipAddr]);

  const handleSubmit = () => {
    if (!deviceName.trim()) {
      toast.error('Device name is required.');
      return;
    }
    if (!ipAddr.trim()) {
      toast.error('IP Address is required.');
      return;
    }
    setLoadingModalOpen(true);
    createDevice.mutate({
      node_id: nodeId,
      device_name: deviceName.trim(),
      ip_addr: ipAddr.trim(),
      ...(macAddr && {mac_addr: macAddr}),
      ...(vendorName && {vendor_name: vendorName}),
    });
  };

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="device_name"
            label="Device Name"
            variant="outlined"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="ip_addr"
            label="IP Address"
            variant="outlined"
            value={ipAddr}
            onChange={(e) => setIpAddr(e.target.value)}
            placeholder="192.168.1.100"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="mac_addr"
            label="MAC Address (Optional)"
            variant="outlined"
            value={macAddr}
            onChange={(e) => setMacAddr(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="vendor_name"
            label="Manufacturer (Optional)"
            variant="outlined"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!isFormValid}>
              Add
            </Button>
          </Box>
        </Grid>
      </Grid>
      {loadingModalOpen && <PageLoading content="Adding a new device..." />}
    </Box>
  );
};
